import React from 'react';
import {
    Checkbox,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    Tooltip,
} from '@onward-delivery/design';
import { EllipsisVertical, Icon } from '@onward-delivery/design/icons';

import BadgeTag from './BadgeTag';
import { describeCarrierRow } from '../utils';

const CardBadge = ({ badge }) => {
    const tag = <BadgeTag variant={badge.variant}>{badge.label}</BadgeTag>;
    // The tooltip anchors to its child by ref, and Tag doesn't forward one, so the span is the anchor.
    return badge.tooltip ? (
        <Tooltip content={badge.tooltip}>
            <span className="inline-flex">{tag}</span>
        </Tooltip>
    ) : (
        tag
    );
};

/**
 * One account's card in an onboarding tab: checkbox, name and subtitle, badges, and the hamburger menu (when it has
 * options). `describe` reads the row: describeCarrierRow by default, describeShipperRow for shippers.
 *
 * @param {{ row: object, checked: boolean, onToggle: Function, onMenuOption?: Function, describe?: Function }} props
 */
const OnboardingRow = ({ row, checked, onToggle, onMenuOption, describe = describeCarrierRow }) => {
    const { id, name, subtitle, badges, options } = describe(row);

    return (
        <li className="flex items-center gap-3 rounded-[10px] border border-slate-200 bg-white px-3.5 py-3">
            <Checkbox checked={checked} onCheckedChange={() => onToggle(id)} aria-label={`Select ${name}`} />
            <div className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-[14px] font-semibold text-slate-900">{name}</span>
                <span className="truncate text-[12px] text-slate-500">{subtitle}</span>
            </div>
            <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
                {badges.map((badge) => (
                    <CardBadge key={badge.key} badge={badge} />
                ))}
            </div>
            {options.length ? (
                <DropdownMenu>
                    <DropdownMenuTrigger
                        aria-label={`Options for ${name}`}
                        className="inline-flex size-8 shrink-0 items-center justify-center rounded-md bg-transparent text-muted hover:bg-muted/10 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    >
                        <Icon icon={EllipsisVertical} size="sm" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        {options.map((option) => (
                            <DropdownMenuItem key={option.key} onSelect={() => onMenuOption(row, option.key)}>
                                {option.label}
                            </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                </DropdownMenu>
            ) : (
                <span className="size-8 shrink-0" aria-hidden="true" />
            )}
        </li>
    );
};

export default OnboardingRow;
